import { ApolloQueryResult } from '@apollo/client';
import { Box, Flex, Spacer, Card, CardBody } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  SuccessPortfolioCovenantGQLType,
  SuccessFinancialCovenantGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartDatePicker } from '../shared/PortfolioChartDatePicker';
import {
  chartsInitialEndDateJs,
  ChartTimePeriod,
  covenantsInitialStartDate,
  covenantsInitialStartDateJs,
  defaultSelectedTimePeriod,
  ExpandedCovenantChartData,
  expandCovenantsChartData,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  onSelectDateRange,
  PortfolioChartConfig,
  getThresholdLevelLineColors,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { PortfolioTimePeriodSelectButtons } from '../shared/PortfolioTimePeriodSelectButtons';
import { CovenantChartDetails } from './CovenantChartDetails';
import { GetSuccessPortfolioCovenantsByCreditFacilityIdQueryGQLType } from '~/packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { getDefaultDashboardDisplayEndDate } from '~/packages/common-frontend/constants';
import { GetSuccessFinancialCovenantsByCreditFacilityIdQueryGQLType } from '~/packages/covenants/requests/financialCovenants.gql.generated';
import {
  getCovenantValueFormula,
  getThresholdLevel,
  getThresholdType,
  isThresholdValueIntermediateField,
} from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantValue/thresholdValueUtils';

type CovenantChartTemplateProps = {
  covenantType: 'portfolio' | 'financial';
  creditFacilityId: string;
  covenantKey: string;
  chartTitle: string;
  chartConfig: PortfolioChartConfig<any>;
  creditAgreementCovenant:
    | CreditAgreement.DomainEntity.PortfolioCovenantProperty
    | CreditAgreement.DomainEntity.FinancialCovenantProperty
    | undefined;
  covenantData: (
    | SuccessPortfolioCovenantGQLType
    | SuccessFinancialCovenantGQLType
  )[];
  refetchCovenants: (variables: {
    [key: string]: any;
  }) => Promise<
    ApolloQueryResult<
      | GetSuccessPortfolioCovenantsByCreditFacilityIdQueryGQLType
      | GetSuccessFinancialCovenantsByCreditFacilityIdQueryGQLType
    >
  >;
};
export const CovenantChartTemplate = ({
  covenantType,
  creditFacilityId,
  covenantKey,
  chartTitle,
  chartConfig,
  creditAgreementCovenant,
  covenantData,
  refetchCovenants,
}: CovenantChartTemplateProps) => {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const formattedChartData = useMemo(() => {
    const dataForCovenantKey = covenantData.filter(
      (covenant) => covenant.covenantKey === covenantKey,
    );
    const expandedCovenantData = expandCovenantsChartData(dataForCovenantKey);

    // we must make sure to set the core date range chart data with all of the data by day
    // this ensures that we have the base by-day data ready to use
    // when going back to a higher-frequency time period view than the currently selected period
    const formattedChartDataForReportingDate = formatChartDataByReportingDate({
      chartData: expandedCovenantData,
      selectedTimePeriod: ChartTimePeriod.day,
      versionFieldName: 'version',
    });

    const formattedChartDataForPeriod =
      selectedTimePeriod !== ChartTimePeriod.day
        ? formatChartDataByReportingDate({
            chartData: expandedCovenantData,
            selectedTimePeriod,
          })
        : formattedChartDataForReportingDate;

    return { formattedChartDataForReportingDate, formattedChartDataForPeriod };
  }, [covenantData, covenantKey, selectedTimePeriod]);

  const {
    formattedChartDataForReportingDate,
    formattedChartDataForPeriod,
  } = formattedChartData;

  const [
    selectedDataPoint,
    setSelectedDataPoint,
  ] = useState<ExpandedCovenantChartData | null>(
    formattedChartDataForReportingDate[
      formattedChartDataForReportingDate.length - 1
    ],
  );
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: covenantsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    ExpandedCovenantChartData[]
  >(formattedChartDataForPeriod);

  // when the input data has changed, update the chart data and selected datapoint
  useEffect(() => {
    setChartDataWithTimePeriod(formattedChartDataForPeriod);
    setSelectedDataPoint(
      formattedChartDataForReportingDate[
        formattedChartDataForReportingDate.length - 1
      ],
    );
  }, [formattedChartDataForPeriod, formattedChartDataForReportingDate]);

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<ExpandedCovenantChartData>(
        chartDataWithTimePeriod,
      ),
    [chartDataWithTimePeriod],
  );

  const thresholdType =
    creditAgreementCovenant && creditAgreementCovenant._config_
      ? getThresholdType(
          covenantType,
          creditAgreementCovenant._config_.intermediateFields,
        )
      : null;

  const covenantValueFormula = useMemo(
    () =>
      getCovenantValueFormula({
        type: covenantType,
        creditAgreementCovenant,
      }),
    [creditAgreementCovenant, covenantType],
  );

  const thresholdLevelsChartConfig = useMemo(() => {
    const thresholdChartConfig: PortfolioChartConfig<
      keyof ExpandedCovenantChartData
    > = {};

    const thresholdIntermediateFieldKeys = _.chain(selectedDataPoint)
      .map((_property, key) =>
        isThresholdValueIntermediateField(key) ? key : undefined,
      )
      .compact()
      .value();

    thresholdIntermediateFieldKeys.sort().forEach((fieldKey, index) => {
      const thresholdLevel = getThresholdLevel(fieldKey);
      const thresholdChartColors = getThresholdLevelLineColors(
        thresholdIntermediateFieldKeys.length,
      );

      thresholdChartConfig[fieldKey] = {
        chartType: 'dashedLine',
        field: fieldKey,
        label: `Level ${thresholdLevel} Threshold`,
        format: chartConfig.value?.format ?? 'percentage',
        color: thresholdChartColors[index] ?? colors.red[600],
      };
    });

    return thresholdChartConfig;
  }, [selectedDataPoint, chartConfig.value?.format]);

  const chartConfigWithThresholds = {
    ...chartConfig,
    ...thresholdLevelsChartConfig,
  };

  const visibleChartComponents: (keyof ExpandedCovenantChartData)[] = useMemo(() => {
    const thresholdLevelFields = _.compact(
      _.map(thresholdLevelsChartConfig, (config) => config?.field),
    );

    const standardFields = ['value'];

    return [...standardFields, ...thresholdLevelFields];
  }, [thresholdLevelsChartConfig]);

  const onSelectTimePeriod = useCallback(
    (timePeriod: ChartTimePeriod) => {
      setSelectedTimePeriod(timePeriod);

      const formattedChartDataByReportingDate = formatChartDataByReportingDate({
        chartData: formattedChartDataForReportingDate,
        selectedTimePeriod: timePeriod,
      });

      setChartDataWithTimePeriod(formattedChartDataByReportingDate);
    },
    [formattedChartDataForReportingDate],
  );

  const header = (
    <Flex mb="40px">
      <PortfolioChartDatePicker
        onSelectDateRange={(newDateRange) => {
          let refetchVariables;
          if (newDateRange[0] && newDateRange[1]) {
            refetchVariables = {
              creditFacilityId,
              startDate: commonDate.dateToFullDateString(newDateRange[0]),
              endDate: commonDate.dateToFullDateString(newDateRange[1]),
            };
          }
          if (!newDateRange[0] && !newDateRange[1]) {
            refetchVariables = {
              creditFacilityId,
              startDate: covenantsInitialStartDate,
              endDate: getDefaultDashboardDisplayEndDate(),
            };
          }
          return onSelectDateRange<
            | GetSuccessPortfolioCovenantsByCreditFacilityIdQueryGQLType
            | GetSuccessFinancialCovenantsByCreditFacilityIdQueryGQLType
          >({
            newDateRange,
            refetch: refetchCovenants,
            refetchVariables,
            creditFacilityId,
            setChartDataDateRange,
          });
        }}
        currentDateRange={chartDataDateRange}
      />

      <Spacer />
      <PortfolioTimePeriodSelectButtons
        currentSelectedTimePeriod={selectedTimePeriod}
        onSelectTimePeriod={onSelectTimePeriod}
      />
    </Flex>
  );
  return (
    <Flex direction="column" gap="1rem">
      <Typography variant="section-title" trim>
        {chartTitle}
      </Typography>
      <Card variant="outline" size="lg">
        <CardBody>
          <Box width="100%">
            {header}
            {/* child 1 */}
            <PortfolioReChart
              chartData={chartDataWithTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedDataPoint
                  ? selectedDataPoint.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={setSelectedDataPoint}
              chartConfig={chartConfigWithThresholds}
              visibleChartComponents={visibleChartComponents}
            />
          </Box>
          <Spacer />
          {/* child 2 */}
          {selectedDataPoint && (
            <Box width="100%" mt="64px">
              <CovenantChartDetails
                selectedDataPoint={selectedDataPoint}
                thresholdType={thresholdType}
                covenantValueFormula={covenantValueFormula}
              />

              <Flex>
                <Spacer />
                <PortfolioDownloadCsvLink
                  dataRows={dataRows}
                  headers={headers}
                  chartTitle={chartTitle}
                  selectedTimePeriod={selectedTimePeriod}
                />
              </Flex>
            </Box>
          )}
        </CardBody>
      </Card>
    </Flex>
  );
};
